import { Button, Icon, Input, Typography } from '@smartgift/frontend-toolkit';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { MAX_FEATURE_NAME_LENGTH } from '@hero/constants';

const EditableArea = styled.div`
  align-items: center;
  border: 1px solid transparent;
  cursor: pointer;
  display: flex;
  gap: 8px;
  height: 100%;
  min-width: max-content;
  padding: 8px;
  padding-left: 16px;
  position: absolute;
  width: 100%;

  &:hover {
    border: 1px solid var(--color-accent-secondary-weaker);
    border-radius: var(--size-border-radius-md);
  }
`;

const ActionButton = styled.button`
  align-items: center;
  background: var(--color-bg-body);
  border: 1px solid var(--color-accent-secondary-weaker);
  border-radius: var(--size-border-radius-md);
  box-shadow: var(--shadow-s);
  cursor: ${({ inactive }) => (inactive ? `not-allowed` : `pointer`)};
  display: flex;
  height: 32px;
  justify-content: center;
  margin-left: 3px;
  width: 32px;

  svg path {
    fill: ${({ inactive }) => (inactive ? `var(--color-accent-secondary-default)` : '')};
  }
`;

const ActionButtons = styled.div`
  bottom: -15px;
  display: flex;
  position: absolute;
  right: -8px;
`;

const DetailItem = styled.div`
  min-width: fit-content;
  position: absolute;
  width: 100%;
`;

const TitleWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  height: 40px;
  min-width: fit-content;
  position: relative;
  width: 100%;

  ${Input.S.Base} {
    width: 100%;
  }
`;

function useSendGiftReviewHeader({ recognition = {}, onUpdate = () => {} }) {
  const { t } = useTranslation();

  const [edit, setEdit] = useState(false);
  const [recognitionName, setRecognitionName] = useState(recognition?.name);
  const [inputRef, setInputRef] = useState(null);

  useEffect(() => {
    inputRef?.focus();
  }, [inputRef]);

  useEffect(() => {
    if (recognition?.name) {
      setRecognitionName(recognition?.name);
    }
  }, [recognition?.name]);

  return {
    title: (
      <TitleWrapper>
        {!edit ? (
          <EditableArea
            onClick={() => {
              setEdit(true);
            }}
          >
            <Typography
              variant="h3"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 'max-content'
              }}
            >
              {recognitionName}
            </Typography>
            <Button variant="link" startIcon={<Icon name="edit" />}>
              {t('edit')}
            </Button>
          </EditableArea>
        ) : (
          <DetailItem>
            <Input
              ref={setInputRef}
              value={recognitionName}
              variant="outlined"
              onChange={e => setRecognitionName(e.target.value)}
              error={!recognitionName}
              inputProps={{ maxLength: MAX_FEATURE_NAME_LENGTH }}
            />
            <ActionButtons>
              <ActionButton
                onClick={() => {
                  setEdit(false);
                  setRecognitionName(recognition?.name);
                }}
              >
                <Icon name="close" />
              </ActionButton>
              <ActionButton
                disabled={!recognitionName}
                onClick={() => {
                  if (recognition?.name !== recognitionName) {
                    onUpdate({ name: recognitionName });
                  }
                  setEdit(false);
                }}
              >
                <Icon name="success" />
              </ActionButton>
            </ActionButtons>
          </DetailItem>
        )}
      </TitleWrapper>
    ),
    ...(recognition?.link?.preview && {
      endButton: {
        children: t('howRecipientsWillSeeThis'),
        icon: <Icon name="preview" />,
        props: {
          color: 'primary',
          variant: 'link'
        },
        onClick: async () => {
          window.open(recognition?.link?.preview, '_blank');
        }
      }
    }),
    showBorder: true,
    style: {
      backgroundColor: 'var(--color-bg-weakest2)'
    }
  };
}

export { useSendGiftReviewHeader };
